/* ---------------------------------
Types (sort of)

Please keep in mind this is just an
attempt to keep things consistent.

It is very much a work in progress,
and mismatches in the PropTypes
attached to components are expected.
--------------------------------- */

import PropTypes from 'prop-types'

export const ImageType = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  alternativeText: PropTypes.string,
  caption: PropTypes.string,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  hash: PropTypes.string,
  ext: PropTypes.string,
  mime: PropTypes.string,
  size: PropTypes.number,
  url: PropTypes.string.isRequired,
  previewUrl: PropTypes.string,
  provider: PropTypes.string,
  provider_metadata: PropTypes.any,
  created_at: PropTypes.string,
  updated_at: PropTypes.string,
})

export const VideoType = PropTypes.shape({
  url: PropTypes.string.isRequired,
  poster: PropTypes.string,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
})

export const CTAType = PropTypes.shape({
  label: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
})

export const TestimonialCardType = PropTypes.shape({
  data: PropTypes.shape({
    id: PropTypes.number,
    attributes: PropTypes.shape({
      title: PropTypes.string,
      subtitle: PropTypes.string,
      image: PropTypes.shape({
        url: PropTypes.string,
      }),
      text: PropTypes.string,
      createdAt: PropTypes.string,
      updatedAt: PropTypes.string,
      publishedAt: PropTypes.string,
    }),
  }),
})

export const ProgressCardType = PropTypes.shape({
  figures: PropTypes.shape({
    part: PropTypes.number.isRequired,
    whole: PropTypes.number.isRequired,
    unit: PropTypes.string.isRequired,
  }).isRequired,
  testimonials: PropTypes.node,
  ctaMessage: PropTypes.string,
  createdAt: PropTypes.string,
  updatedAt: PropTypes.string,
})

export const SlideType = PropTypes.shape({
  type: PropTypes.oneOf(['video', 'image']).isRequired,
  title: PropTypes.string,
  body: PropTypes.string,
  cta: CTAType,
  image: ImageType,
  video: VideoType,
  testimonialCard: TestimonialCardType,
})

/**
 * @typedef {Object} RequiredIfCondition
 * @property {string} field - The field name to watch
 * @property {*} [equals] - The value that makes this field required (exact match)
 * @property {Array} [includes] - Array of values, field is required if watched field value is in this array
 * @property {*} [notEquals] - The value that when NOT matched makes this field required
 * @property {string} [message] - Custom validation message when field is required but empty
 */

/**
 * @typedef {Object} RequiredIfMultiCondition
 * @property {Array<RequiredIfCondition>} fields - Array of conditions that must ALL be true (AND logic)
 * @property {string} [message] - Custom validation message when field is required but empty
 */

/**
 * @typedef {Object} FieldConfig
 * @property {boolean|{value: boolean, message?: string}} [required] - Standard required validation
 * @property {RequiredIfCondition|RequiredIfMultiCondition} [requiredIf] - Conditional required validation based on other field values
 * @property {{value: number, message?: string}} [minLength] - Minimum length validation
 * @property {{value: number, message?: string}} [maxLength] - Maximum length validation
 * @property {{value: number, message?: string}} [min] - Minimum value validation
 * @property {{value: number, message?: string}} [max] - Maximum value validation
 * @property {{value: string|RegExp, message?: string}} [pattern] - Pattern validation (regex or string identifier)
 * @property {Object<string, Function>} [validate] - Custom validation functions
 * @property {boolean} [valuesAsDate] - Parse value as date
 */

/**
 * @typedef {Object} WatchFieldCondition
 * @property {string} field - The field name to watch
 * @property {*} [equals] - Show this field only if watched field equals this value
 * @property {Array} [includes] - Show this field only if watched field value is in this array
 */

/**
 * @typedef {Object} FieldUIOptions
 * @property {number} [step] - The form step where this field appears
 * @property {number} [edit] - The edit group for this field
 * @property {boolean} [hidden] - Whether the field is hidden
 * @property {boolean} [showLabel] - Whether to show the field label
 * @property {boolean} [showSubLabel] - Whether to show the field sublabel
 * @property {boolean} [showAvatars] - Whether to show avatar decorations
 * @property {string} [group] - Visual grouping identifier
 * @property {string} [groupLabel] - Label for the field group
 * @property {string} [editGroupLabel] - Label for the edit group
 * @property {string} [recapLabel] - Label to show in recap/summary
 * @property {Array<WatchFieldCondition>} [watchFields] - Conditions for field visibility
 * @property {Array<{fieldName: string, type: string}>} [autoCompleteAddress] - Address autocomplete configuration
 */

/**
 * @typedef {Object} FormField
 * @property {string} fieldName - Unique identifier for the field
 * @property {string} inputType - Type of input (text, email, select, radio-group, checkbox, etc.)
 * @property {string} [label] - Display label for the field
 * @property {string} [subLabel] - Additional description or help text
 * @property {string} [placeholder] - Placeholder text for input fields
 * @property {*} [defaultValue] - Default value for the field
 * @property {FieldConfig} [config] - Validation configuration
 * @property {FieldUIOptions} [uiOptions] - UI and behavior options
 * @property {Array<{value: string, label: string}>} [radioOptions] - Options for radio group inputs
 * @property {Object} [radioOptionsByField] - Dynamic radio options based on other fields
 * @property {Array<{value: string, label: string}>} [selectOptions] - Options for select inputs
 * @property {string} [content] - Content for checkbox fields
 * @property {number} [maxInputList] - Maximum items for input-adder
 * @property {string} [errorLabel] - Custom error label for input-adder
 */
